import { z } from 'zod';
/**
 * Phase 3c.1 — public booking submission + verification payloads.
 *
 * Visitor flow:
 *   1. POST /booking/:slug/submit → creates a booking_verification with a
 *      6-digit code, emails the visitor.
 *   2. Visitor enters code → POST /booking/:slug/verify → creates the
 *      events row (status 'pending' or 'pending_approval') and sends
 *      confirmation email + .ics.
 */
const phoneRefine = (v) => {
    const d = v.replace(/\D+/g, '');
    return d.length === 10 || (d.length === 11 && d.startsWith('1'));
};
const PHONE_MSG = 'phone must be a 10- or 11-digit NANP number (e.g. 5551234567 or +15551234567)';
export const BookingSubmitSchema = z.object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    /** "HH:MM" in the customer's local time as defined by service_profile.business_hours */
    slot_time: z.string().regex(/^\d{2}:\d{2}$/),
    name: z.string().min(1).max(200),
    email: z.string().email(),
    phone: z.string().min(1).max(32).refine(phoneRefine, { message: PHONE_MSG }),
    /** Custom field values keyed by FormField.key */
    custom_fields: z.record(z.string(), z.union([z.string(), z.boolean()])).default({}),
    /** Browse-auth JWT issued by POST /booking/:slug/browse-auth, if profile requires it */
    browse_token: z.string().optional(),
});
export const BookingSubmitResponseSchema = z.object({
    verification_id: z.string().regex(/^bv_[A-Za-z0-9_-]+$/),
    email: z.string().email(),
    /** Seconds until the verification code expires. */
    expires_in_seconds: z.number().int().positive(),
});
export const BookingVerifyRequestSchema = z.object({
    verification_id: z.string().regex(/^bv_[A-Za-z0-9_-]+$/),
    code: z.string().regex(/^\d{6}$/),
});
export const BookingVerifyResponseSchema = z.object({
    event_id: z.string().regex(/^evt_[A-Za-z0-9_-]+$/),
    status: z.enum(['pending', 'pending_approval']),
    redirect_url: z.string().url().nullable(),
});
export const BookingSlotSchema = z.object({
    /** "HH:MM" local time per business_hours */
    start_time: z.string().regex(/^\d{2}:\d{2}$/),
    end_time: z.string().regex(/^\d{2}:\d{2}$/),
});
export const BookingSlotsResponseSchema = z.object({
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    slots: z.array(BookingSlotSchema),
});
export const BrowseAuthRequestSchema = z.object({
    password: z.string().min(1).max(256),
});
export const BrowseAuthResponseSchema = z.object({
    /** Short-lived (15min) JWT to attach as `browse_token` on subsequent calls. */
    browse_token: z.string(),
    expires_in_seconds: z.number().int().positive(),
});
export const EmbedSnippetsSchema = z.object({
    iframe: z.string(),
    inline: z.string(),
});
//# sourceMappingURL=booking.js.map